import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import TrainerLayout from './TrainerLayout';
import InviteOwnerModal from './InviteOwnerModal';
import SearchBar from '../../components/commons/searchbar/SearchBar';
import '../../styles/OwnersList.css';

const OwnersList = () => {
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();

    const [owners, setOwners] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        if (!trainer || !trainer.schoolId) return;

        const fetchOwners = async () => {
            try {
                setLoading(true);
                let endpoint = `/api/v1/owner/all/${trainer.schoolId}?page=${currentPage}&size=${pageSize}`;

                if (searchTerm.trim()) {
                    endpoint += `&search=${encodeURIComponent(searchTerm.trim())}`;
                    setIsSearching(true);
                } else {
                    setIsSearching(false);
                }

                const response = await axios.get(endpoint, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
                });

                setOwners(response.data.content || []);
                setTotalPages(response.data.totalPages || 1);
            } catch (err) {
                setError('Nie udało się pobrać właścicieli.');
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchOwners();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [trainer, currentPage, pageSize, searchTerm]);

    const formatAddress = (address) => {
        if (!address) return 'Brak danych';
        return `${address.street} ${address.buildingNumber}${address.apartmentNumber ? '/' + address.apartmentNumber : ''}, ${address.postalCode} ${address.city}, ${address.country}`;
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return (
        <TrainerLayout pageTitle="Wszyscy właściciele">
            <div className="owners-page-container">
                <h1 className="owners-mobile-title">Właściciele</h1>

                <div className="owners-search-section">
                    <div className="owners-search-and-button-group">
                        <SearchBar
                            width="400px"
                            value={searchTerm}
                            onChange={setSearchTerm}
                            onSearch={() => setCurrentPage(0)}
                            placeholder={{
                                mobile: "Szukaj",
                                desktop: "Szukaj po imieniu, nazwisku lub emailu"
                            }}
                        />

                        <button
                            className="owners-invite-button"
                            onClick={openModal}
                        >
                            Zaproś właściciela
                        </button>
                    </div>

                    <div className="owners-pagination-controls-inline">
                        <label className="owners-page-size-label">Na stronę:</label>
                        <select
                            className="owners-page-size-select"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(0);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <p className="owners-loading-text">Ładowanie...</p>
                ) : error ? (
                    <p className="owners-error-message">{error}</p>
                ) : (
                    <>
                        {isSearching && owners.length > 0 && (
                            <div className="owners-search-results-info">
                                Znaleziono: {owners.length} {owners.length === 1 ? 'wynik' :
                                owners.length < 5 ? 'wyniki' : 'wyników'}
                            </div>
                        )}

                        <div className="owners-table-section">
                            <table className="owners-table">
                                <thead>
                                <tr>
                                    <th>Imię i Nazwisko</th>
                                    <th>Email</th>
                                    <th>Telefon</th>
                                    <th>Adres</th>
                                </tr>
                                </thead>
                                <tbody>
                                {owners.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="owners-no-data-row">
                                            {isSearching
                                                ? 'Brak wyników wyszukiwania'
                                                : 'Brak właścicieli'}
                                        </td>
                                    </tr>
                                ) : (
                                    owners.map((owner) => (
                                        <tr
                                            key={owner.id}
                                            onClick={() => navigate(`/owner/details/${owner.id}`)}
                                            className="owners-clickable-row"
                                        >
                                            <td className="owners-centered-column">
                                                <strong>{owner.firstName && owner.lastName ? `${owner.firstName} ${owner.lastName}` : owner.username}</strong>
                                            </td>
                                            <td className="owners-centered-column">{owner.email}</td>
                                            <td className="owners-centered-column">{owner.phone}</td>
                                            <td className="owners-centered-column">{formatAddress(owner.address)}</td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>

                        <div className="owners-mobile-cards">
                            {owners.length === 0 ? (
                                <div className="owners-mobile-no-data">
                                    {isSearching ? 'Brak wyników wyszukiwania' : 'Brak właścicieli'}
                                </div>
                            ) : (
                                owners.map((owner) => (
                                    <div
                                        key={owner.id}
                                        className="owners-card"
                                        onClick={() => navigate(`/owner/details/${owner.id}`)}
                                    >
                                        <div className="owners-card-header">
                                            <h3 className="owners-name">
                                                {owner.firstName && owner.lastName ? `${owner.firstName} ${owner.lastName}` : owner.username}
                                            </h3>
                                        </div>
                                        <div className="owners-details">
                                            <div className="owners-detail-item">
                                                <span className="owners-detail-label">Email</span>
                                                <span className="owners-detail-value">{owner.email}</span>
                                            </div>
                                            <div className="owners-detail-item">
                                                <span className="owners-detail-label">Telefon</span>
                                                <span className="owners-detail-value">{owner.phone}</span>
                                            </div>
                                            <div className="owners-detail-item owners-address">
                                                <span className="owners-detail-label">Adres</span>
                                                <span className="owners-detail-value">{formatAddress(owner.address)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="owners-pagination-section">
                            <button
                                className="owners-pagination-btn"
                                disabled={currentPage === 0}
                                onClick={() => setCurrentPage(currentPage - 1)}
                            >
                                Poprzednia
                            </button>
                            <span className="owners-page-info">
                                Strona {currentPage + 1} z {totalPages}
                            </span>
                            <button
                                className="owners-pagination-btn"
                                disabled={currentPage + 1 >= totalPages}
                                onClick={() => setCurrentPage(currentPage + 1)}
                            >
                                Następna
                            </button>
                        </div>
                    </>
                )}

                <InviteOwnerModal isOpen={isModalOpen} onClose={closeModal} />
            </div>
        </TrainerLayout>
    );
};

export default OwnersList;